import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import { ServerMonitoringResponse } from 'src/app/core/interfaces/server-monitoring/server-monitoring.interface';
import {
  SessionResponseInterface,
  PremiumBillingOverview,
  PremiumOpenWithdrawal,
  PremiumWithdrawalPreview,
  PremiumWithdrawalResult,
  PremiumGoodwillResult,
  PremiumSubscriptionAction,
  PremiumBillingSubscription,
} from 'src/app/core/interfaces';
import {
  UserOverviewResponse,
  StatsResponse,
  OnlineStatsResponse,
  DonationOverviewResponse
} from 'src/app/core/interfaces';

@Injectable({
  providedIn: 'root',
})
export class AdminBackendService {
  private readonly BASE_ADDRESS = '/admin';

  constructor(
    private http: HttpBackendService,
    private httpClient: HttpClient
  ) {}

  public getStats(days?: number): Promise<StatsResponse> {
    const params = days ? { days: days.toString() } : {};
    return firstValueFrom(
      this.http.get<StatsResponse>(`${this.BASE_ADDRESS}/stats`, { params })
    );
  }

  public getUserOverview(): Promise<UserOverviewResponse> {
    return firstValueFrom(
      this.http.get<UserOverviewResponse>(`${this.BASE_ADDRESS}/user-overview`)
    );
  }

  public getOnlineStats(params?: {
    from?: string;
    to?: string;
    hours?: number;
  }): Promise<OnlineStatsResponse> {
    const queryParams: any = {};
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== undefined) {
          queryParams[key] = value.toString();
        }
      });
    }
    return firstValueFrom(
      this.http.get<OnlineStatsResponse>(`${this.BASE_ADDRESS}/online-stats`, { params: queryParams })
    );
  }

  public getStatistics(): Promise<any> {
    return firstValueFrom(this.http.get<any>('/statistics'));
  }

  public getNewUserFunnel(days?: number): Promise<any> {
    const params: any = {};
    if (days) {
      params.days = days.toString();
    }
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/new-user-funnel`, { params })
    );
  }

  public resolveInsults(date: string, messageIDs: number[]): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/chat/resolve-insults`, {
        date,
        messageIDs,
      })
    );
  }

  public getInsultsToday(): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/chat/get-insults-today`)
    );
  }

  public getInsults(date?: string): Promise<{
    date: string;
    count: number;
    results: any[];
  }> {
    const params: any = {};
    if (date) params.date = date;
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/chat/get-insults`, { params })
    );
  }

  public getChatStats(date?: string): Promise<{
    date: string;
    totalMessages: number;
    insultCount: number;
    uniqueUsers: number;
  }> {
    const params: any = {};
    if (date) params.date = date;
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/chat/get-stats`, { params })
    );
  }

  public getUserInsults(userID: number, daysBack?: number): Promise<any> {
    const params: any = { userID: userID.toString() };
    if (daysBack) params.daysBack = daysBack.toString();
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/chat/get-user-insults`, {
        params,
      })
    );
  }

  public searchMessages(
    userID?: number,
    table?: string,
    limit?: number,
    offset?: number,
    sort?: string,
    aroundMessageID?: number
  ): Promise<any> {
    const params: any = {};
    if (userID) params.userID = userID.toString();
    if (table) params.table = table;
    if (limit) params.limit = limit.toString();
    if (offset) params.offset = offset.toString();
    if (sort) params.sort = sort;
    if (aroundMessageID) params.aroundMessageID = aroundMessageID.toString();
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/chat/search-messages`, {
        params,
      })
    );
  }

  public getContactMessage(id: number): Promise<any> {
    const params = { id: id.toString() };
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/contact-message/get`, { params })
    );
  }

  public listContactMessages(
    isRead?: boolean,
    isHandled?: boolean,
    limit?: number,
    offset?: number,
    sort?: string,
    isImportant?: boolean
  ): Promise<any> {
    const params: any = {};
    if (isRead !== undefined) params.isRead = isRead.toString();
    if (isHandled !== undefined) params.isHandled = isHandled.toString();
    if (limit) params.limit = limit.toString();
    if (offset) params.offset = offset.toString();
    if (sort) params.sort = sort;
    if (isImportant !== undefined) params.isImportant = isImportant.toString();
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/contact-message/list`, {
        params,
      })
    );
  }

  public getContactMessageCount(filter: { isRead?: boolean; isHandled?: boolean } = {}): Promise<{ count: number }> {
    const params: any = {};
    if (filter.isRead !== undefined) params.isRead = filter.isRead.toString();
    if (filter.isHandled !== undefined) params.isHandled = filter.isHandled.toString();
    return firstValueFrom(
      this.http.get<{ count: number }>(`${this.BASE_ADDRESS}/contact-message/count`, {
        params,
      })
    );
  }

  public sendContactReply(id: number, replyText: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/contact-message/send-reply`, {
        id,
        replyText,
      })
    );
  }

  public updateContactMessage(
    id: number,
    isRead?: boolean,
    isHandled?: boolean,
    sentAnswers?: string,
    isImportant?: boolean,
    handlingReason?: 'handled' | 'outdated' | 'withdrawn' | 'withdrawalDismissed'
  ): Promise<any> {
    const data: any = { id };
    if (isRead !== undefined) data.isRead = isRead;
    if (isHandled !== undefined) data.isHandled = isHandled;
    if (sentAnswers) data.sentAnswers = sentAnswers;
    if (isImportant !== undefined) data.isImportant = isImportant;
    if (handlingReason !== undefined) data.handlingReason = handlingReason;
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/contact-message/update`, data)
    );
  }

  // ===== BUG REPORT ENDPOINTS =====

  public listBugReports(
    status?: 'open' | 'investigating' | 'closed',
    priority?: 'low' | 'medium' | 'high' | 'critical',
    assignedTo?: number,
    userID?: number,
    isDeleted?: boolean,
    limit?: number,
    offset?: number,
    sort?: string,
    reportType?: 'manual' | 'triggered_by_chat' | 'auto_server_error',
    date?: string
  ): Promise<any> {
    const params: any = {};
    if (status) params.status = status;
    if (priority) params.priority = priority;
    if (assignedTo) params.assignedTo = assignedTo.toString();
    if (userID) params.userID = userID.toString();
    if (isDeleted !== undefined) params.isDeleted = isDeleted.toString();
    if (limit) params.limit = limit.toString();
    if (offset) params.offset = offset.toString();
    if (sort) params.sort = sort;
    if (reportType) params.reportType = reportType;
    if (date) params.date = date;
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/bug-reports/list`, { params })
    );
  }

  public getBugReport(id: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/bug-reports/${id}`)
    );
  }

  public updateBugReportStatus(
    id: number,
    status: 'open' | 'investigating' | 'closed',
    adminNotes?: string
  ): Promise<any> {
    const data: any = { status };
    if (adminNotes) data.adminNotes = adminNotes;
    return firstValueFrom(
      this.http.put<any>(`${this.BASE_ADDRESS}/bug-reports/${id}/status`, data)
    );
  }

  public updateBugReport(
    id: number,
    priority?: 'low' | 'medium' | 'high' | 'critical',
    assignedTo?: number | null,
    adminNotes?: string
  ): Promise<any> {
    const data: any = {};
    if (priority) data.priority = priority;
    if (assignedTo !== undefined) data.assignedTo = assignedTo;
    if (adminNotes) data.adminNotes = adminNotes;
    return firstValueFrom(
      this.http.put<any>(`${this.BASE_ADDRESS}/bug-reports/${id}`, data)
    );
  }

  public deleteBugReport(id: number): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(`${this.BASE_ADDRESS}/bug-reports/${id}`)
    );
  }

  public searchBugReports(
    query: string,
    status?: 'open' | 'investigating' | 'closed',
    priority?: 'low' | 'medium' | 'high' | 'critical',
    limit?: number,
    offset?: number,
    reportType?: 'manual' | 'triggered_by_chat' | 'auto_server_error',
    date?: string
  ): Promise<any> {
    const params: any = { query };
    if (status) params.status = status;
    if (priority) params.priority = priority;
    if (limit) params.limit = limit.toString();
    if (offset) params.offset = offset.toString();
    if (reportType) params.reportType = reportType;
    if (date) params.date = date;
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/bug-reports/search`, { params })
    );
  }

  public getBugReportStats(days?: number): Promise<any> {
    const params: any = {};
    if (days) params.days = days.toString();
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/bug-reports/stats`, { params })
    );
  }

  public getBugReportsTimeline(
    from?: string,
    to?: string,
    days?: number,
    reportType?: 'all' | 'manual' | 'triggered_by_chat' | 'auto_server_error'
  ): Promise<any> {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (days) params.days = days.toString();
    if (reportType) params.reportType = reportType;
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/bug-reports/timeline`, { params })
    );
  }

  public addDonation(
    userID: number,
    amount: number,
    paymentDate: string
  ): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/donation/add`, {
        userID,
        amount,
        paymentDate,
      })
    );
  }

  public deleteDonation(id: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/donation/delete`, { id })
    );
  }

  public getDonationTotal(): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/donation/get-total`)
    );
  }

  public getDonationOverview(): Promise<DonationOverviewResponse> {
    return firstValueFrom(
      this.http.get<DonationOverviewResponse>(`${this.BASE_ADDRESS}/donation/overview`)
    );
  }

  public getDonation(id: number): Promise<any> {
    const params = { id: id.toString() };
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/donation/get`, { params })
    );
  }

  public listDonations(
    limit?: number,
    offset?: number,
    sort?: string,
    confirmed?: boolean
  ): Promise<any> {
    const params: any = {};
    if (limit) params.limit = limit.toString();
    if (offset) params.offset = offset.toString();
    if (sort) params.sort = sort;
    if (confirmed !== undefined) params.confirmed = confirmed.toString();
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/donation/list`, { params })
    );
  }

  public updateDonation(
    id: number,
    amount?: number,
    paymentDate?: string,
    userID?: number
  ): Promise<any> {
    const data: any = { id };
    if (amount) data.amount = amount;
    if (paymentDate) data.paymentDate = paymentDate;
    if (userID) data.userID = userID;
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/donation/update`, data)
    );
  }

  public changeUserEmail(userID: number, email: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/change-email`, {
        userID,
        email,
      })
    );
  }

  public changeUserUsername(userID: number, username: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/change-username`, {
        userID,
        username,
      })
    );
  }

  public deleteUser(id: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/delete`, { id })
    );
  }

  public getUserDetails(userID: number): Promise<any> {
    const params = { userID: userID.toString() };
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/user/get-user-details`, {
        params,
      })
    );
  }

  public getUserSessions(userID: number): Promise<SessionResponseInterface> {
    const params = { userID: userID.toString() };
    return firstValueFrom(
      this.http.get<SessionResponseInterface>(`${this.BASE_ADDRESS}/user/get-user-sessions`, {
        params,
      })
    );
  }

  public grantPremium(
    userID: number,
    grant: { mode: 'months'; months: 1 | 3 | 12 } | { mode: 'until'; until: string } | { mode: 'revoke' }
  ): Promise<{ userID: number; premiumUntil: string; isPremium: boolean }> {
    return firstValueFrom(
      this.http.post<{ userID: number; premiumUntil: string; isPremium: boolean }>(
        `${this.BASE_ADDRESS}/premium/grant`,
        { userID, ...grant }
      )
    );
  }

  /** premium & payments of one user (DB + live Stripe, warnings) */
  public getPremiumBilling(userID: number): Promise<PremiumBillingOverview> {
    return firstValueFrom(
      this.http.get<PremiumBillingOverview>(`${this.BASE_ADDRESS}/premium/billing`, {
        params: { userID: userID.toString() },
      })
    );
  }

  /** withdrawal form submissions not processed yet, across all users */
  public getOpenPremiumWithdrawals(): Promise<PremiumOpenWithdrawal[]> {
    return firstValueFrom(this.http.get<PremiumOpenWithdrawal[]>(`${this.BASE_ADDRESS}/premium/open-withdrawals`));
  }

  /** what "Widerruf abwickeln" would do (changes nothing); withdrawnAt default: form submission / now */
  public previewPremiumWithdrawal(userID: number, paymentId: number, withdrawnAt?: string): Promise<PremiumWithdrawalPreview> {
    const params: Record<string, string> = { userID: userID.toString(), paymentId: paymentId.toString() };
    if (withdrawnAt) params['withdrawnAt'] = withdrawnAt;
    return firstValueFrom(
      this.http.get<PremiumWithdrawalPreview>(`${this.BASE_ADDRESS}/premium/withdrawal-preview`, { params })
    );
  }

  public executePremiumWithdrawal(
    userID: number,
    paymentId: number,
    withdrawnAt: string,
    refundCents: number
  ): Promise<PremiumWithdrawalResult> {
    return firstValueFrom(
      this.http.post<PremiumWithdrawalResult>(`${this.BASE_ADDRESS}/premium/withdrawal`, {
        userID, paymentId, withdrawnAt, refundCents,
      })
    );
  }

  public premiumGoodwillRefund(refund: {
    userID: number;
    paymentId?: number;
    invoiceId?: string;
    amountCents: number;
    expectedRefundedCents: number;
    note: string;
  }): Promise<PremiumGoodwillResult> {
    return firstValueFrom(
      this.http.post<PremiumGoodwillResult>(`${this.BASE_ADDRESS}/premium/goodwill-refund`, refund)
    );
  }

  public premiumSubscriptionAction(
    userID: number,
    subscriptionId: string,
    action: PremiumSubscriptionAction
  ): Promise<{ subscription: PremiumBillingSubscription }> {
    return firstValueFrom(
      this.http.post<{ subscription: PremiumBillingSubscription }>(`${this.BASE_ADDRESS}/premium/subscription`, {
        userID, subscriptionId, action,
      })
    );
  }

  /**
   * locks the account; with `reason` the user also gets the statement of reasons (Art. 17 DSA) by e-mail
   * @param automated - the content was flagged by the automatic word list
   */
  public lockUser(id: number, reason?: string, automated = false): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/lock-user`, { id, reason, automated, notifyUser: true })
    );
  }

  /** statement of reasons (Art. 17 DSA) for a moderation decision other than a lock */
  public sendModerationNotice(id: number, decision: string, reason: string, onNotice = false): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/send-moderation-notice`, { id, decision, reason, onNotice })
    );
  }

  public logoffUser(userID: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/logoff-user`, { userID })
    );
  }

  public searchUsers(
    query?: string,
    userID?: number,
    onlyActive?: boolean,
    createdAfter?: string,
    createdBefore?: string,
    limit?: number,
    offset?: number,
    sort?: string
  ): Promise<any> {
    const params: any = {};
    if (query) params.query = query;
    if (userID) params.userID = userID.toString();
    if (onlyActive !== undefined) params.onlyActive = onlyActive.toString();
    if (createdAfter) params.createdAfter = createdAfter;
    if (createdBefore) params.createdBefore = createdBefore;
    if (limit) params.limit = limit.toString();
    if (offset) params.offset = offset.toString();
    if (sort) params.sort = sort;
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/user/search`, { params })
    );
  }

  public sendPasswordResetEmail(userID: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.BASE_ADDRESS}/user/send-password-reset-email`,
        { userID }
      )
    );
  }

  public sendSystemMessage(message: string, userID: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/send-system-message`, {
        message,
        userID,
      })
    );
  }

  public setUserPassword(id: number, password: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/set-password`, {
        id,
        password,
      })
    );
  }

  public generateRandomPassword(userID: number): Promise<{
    message: string;
    userID: number;
    username: string;
    generatedPassword: string;
  }> {
    return firstValueFrom(
      this.http.post<{
        message: string;
        userID: number;
        username: string;
        generatedPassword: string;
      }>(`${this.BASE_ADDRESS}/user/generate-random-password`, { userID })
    );
  }

  public updateUser(
    id: number,
    isLocked?: boolean,
    birthday?: string,
    notes?: string
  ): Promise<any> {
    const data: any = { id };
    if (isLocked !== undefined) data.isLocked = isLocked;
    if (birthday) data.birthday = birthday;
    if (notes) data.notes = notes;
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/update`, data)
    );
  }

  // ===== NEW ANALYTICS ENDPOINTS =====

  public getGameStatistics(params?: {
    period?: string;
    startDate?: string;
    endDate?: string;
    gameType?: string;
    includeNPCGames?: boolean;
    userIds?: number[];
    excludeGuests?: boolean;
    onlyActiveUsers?: boolean;
    metrics?: string[];
    groupBy?: string;
    limit?: number;
    offset?: number;
    format?: string;
    includeVisualization?: boolean;
  }): Promise<any> {
    const queryParams: any = {};
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== undefined) {
          if (Array.isArray(value)) {
            queryParams[key] = value;
          } else {
            queryParams[key] = value.toString();
          }
        }
      });
    }
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/game-statistics`, {
        params: queryParams,
      })
    );
  }

  public getChurnAnalysis(params?: {
    timeWindow?: number;
    includeHistorical?: boolean;
    userSegment?: string;
    minGamesPlayed?: number;
    riskFactors?: string[];
    includeRecommendations?: boolean;
    includeUserList?: boolean;
    format?: string;
  }): Promise<any> {
    const queryParams: any = {};
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== undefined) {
          if (Array.isArray(value)) {
            queryParams[key] = value;
          } else {
            queryParams[key] = value.toString();
          }
        }
      });
    }
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/churn-analysis`, {
        params: queryParams,
      })
    );
  }

  public getRevenueAnalytics(params?: {
    period?: string;
    startDate?: string;
    endDate?: string;
    analysisType?: string[];
    userSegment?: string;
    includeRefunds?: boolean;
    compareWith?: string;
    includePredictions?: boolean;
    format?: string;
  }): Promise<any> {
    const queryParams: any = {};
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== undefined) {
          if (Array.isArray(value)) {
            queryParams[key] = value;
          } else {
            queryParams[key] = value.toString();
          }
        }
      });
    }
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/revenue-analytics`, {
        params: queryParams,
      })
    );
  }

  public createAlert(config: {
    name: string;
    description?: string;
    query: string;
    trigger: any;
    schedule: any;
    notifications: any;
    actions?: any;
    isActive: boolean;
  }): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/alerts`, config)
    );
  }

  public exportReport(
    reportId: string,
    options?: {
      format?: string;
      includeCharts?: boolean;
      includeRawData?: boolean;
      template?: string;
      emailTo?: string[];
    }
  ): Promise<any> {
    const queryParams: any = {};
    if (options) {
      Object.keys(options).forEach((key) => {
        const value = (options as any)[key];
        if (value !== undefined) {
          if (Array.isArray(value)) {
            queryParams[key] = value;
          } else {
            queryParams[key] = value.toString();
          }
        }
      });
    }
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/export/${reportId}`, {
        params: queryParams,
      })
    );
  }

  // ===== MAINTENANCE MODE ENDPOINTS =====

  public getMaintenanceStatus(): Promise<any> {
    return firstValueFrom(this.httpClient.get<any>('/maintenance.json'));
  }

  public setMaintenanceMessage(
    message: string,
    enabled?: boolean,
    additionalConfig?: {
      type?: 'planned' | 'emergency' | 'update';
      severity?: 'info' | 'warning' | 'critical';
      estimatedDuration?: number;
      scheduledStart?: string;
      scheduledEnd?: string;
      allowAdminBypass?: boolean;
      showCountdown?: boolean;
      upcomingBanner?: boolean;
      affectedServices?: string[];
    }
  ): Promise<any> {
    const data: any = { message };
    if (enabled !== undefined) data.enabled = enabled;
    
    // Add additional configuration if provided
    if (additionalConfig) {
      Object.assign(data, additionalConfig);
    }
    
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/maintenance/set-message`, data)
    );
  }

  // ===== IMPERSONATION ENDPOINTS =====

  public impersonateUser(userID: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/impersonate`, { userID })
    );
  }

  public stopImpersonation(): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/user/stop-impersonation`, {})
    );
  }

  // ===== LOG ANALYSIS ENDPOINTS =====

  public getLogAnalysis(): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/logs/analysis`)
    );
  }

  public getLogAnalysisHistory(limit?: number): Promise<any> {
    const params: any = {};
    if (limit) params.limit = limit.toString();
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/logs/analysis-history`, { params })
    );
  }

  public analyzeLogsNow(): Promise<any> {
    return firstValueFrom(
      this.http.post<any>('/internal/analyze-logs', {})
    );
  }

  // ===== SERVER MONITORING ENDPOINTS =====

  public getServerMonitoring(): Promise<ServerMonitoringResponse> {
    return firstValueFrom(
      this.http.get<ServerMonitoringResponse>(`${this.BASE_ADDRESS}/server-monitoring`)
    );
  }

  /**
   * @description logout specific session for any user (admin only)
   * @param {number} userID - user ID
   * @param {string} sessionId - session ID to logout
   * @returns {Promise<any>}
   */
  public logoutUserSession(userID: number, sessionId: string): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(`${this.BASE_ADDRESS}/user/${userID}/sessions/${sessionId}`)
    );
  }

  // ===== LOG VIEWER ENDPOINTS =====

  public listLogs(date?: string): Promise<{
    status: string;
    total: number;
    logs: Array<{
      name: string;
      path: string;
      type: 'system' | 'application' | 'cronjob';
      size?: number;
      lastModified?: string;
    }>;
  }> {
    const params: any = {};
    if (date) {
      params.date = date;
    }
    return firstValueFrom(
      this.http.get<any>('/internal/logs', { params })
    );
  }

  public getLogEntries(logName: string, date: string): Promise<{
    status: string;
    log_name: string;
    log_type: string;
    date: string;
    lines: string[];
    total_lines: number;
  }> {
    return firstValueFrom(
      this.http.get<any>(`/internal/logs/${logName}/${date}`)
    );
  }

  public getMergedLogEntries(date: string): Promise<{
    status: string;
    date: string;
    lines: string[];
    total_lines: number;
    sources: string[];
  }> {
    return firstValueFrom(
      this.http.get<any>(`/internal/logs/merged/${date}`)
    );
  }
}
